import { Box, alpha } from '@mui/material'

export const SEV = {
  critical: '#ff1744',
  high: '#f33b59',
  medium: '#ff9800',
  low: '#ffd54f'
}

export const SEV_INK = {
  critical: '#fff',
  high: '#fff',
  medium: '#1a1208',
  low: '#1a1608'
}

export const UPGRADE = {
  behind: '#ffb74d',
  ok: '#69f0ae',
  unknown: '#90a4ae',
  missing: '#ffb74d'
}

export const UPGRADE_INK = {
  behind: '#1a1208',
  ok: '#06140c',
  unknown: '#101218',
  missing: '#1a1208'
}

/** Solid severity badges — high contrast on dark board surfaces */
export const SeverityBadge = ({
  shortLabel,
  label,
  value,
  color,
  ink = '#fff',
  dense = false
}) => {
  const n = Number(value) || 0
  const hot = n > 0
  return (
    <Box
      title={`${label}: ${n.toLocaleString()}`}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: dense ? 0.4 : 0.55,
        px: dense ? 0.7 : 0.95,
        py: dense ? 0.3 : 0.45,
        minWidth: dense ? 40 : 64,
        justifyContent: 'center',
        borderRadius: 1.25,
        backgroundColor: hot ? color : alpha('#0a0c10', 0.85),
        color: hot ? ink : alpha('#fff', 0.38),
        border: hot
          ? `1px solid ${alpha('#fff', 0.22)}`
          : `1px solid ${alpha('#fff', 0.1)}`,
        boxShadow: hot
          ? `0 2px 10px ${alpha(color, 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.22)}`
          : `inset 0 1px 0 ${alpha('#fff', 0.04)}`
      }}
    >
      <Box
        component='span'
        sx={{
          fontWeight: 900,
          fontSize: dense ? '0.62rem' : '0.68rem',
          letterSpacing: dense ? 0.2 : 0.35,
          textTransform: 'uppercase',
          lineHeight: 1,
          opacity: hot ? 0.95 : 0.7
        }}
      >
        {dense ? shortLabel : label}
      </Box>
      <Box
        component='span'
        sx={{
          fontWeight: 900,
          fontSize: dense ? '0.82rem' : '0.92rem',
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums'
        }}
      >
        {n.toLocaleString()}
      </Box>
    </Box>
  )
}

export const ScorePills = ({ severityCounts = {}, dense = false, includeLow = !dense }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: dense ? 0.5 : 0.65,
      alignItems: 'center'
    }}
  >
    <SeverityBadge
      dense={dense}
      shortLabel='C'
      label='Critical'
      value={severityCounts.critical}
      color={SEV.critical}
      ink={SEV_INK.critical}
    />
    <SeverityBadge
      dense={dense}
      shortLabel='H'
      label='High'
      value={severityCounts.high}
      color={SEV.high}
      ink={SEV_INK.high}
    />
    <SeverityBadge
      dense={dense}
      shortLabel='M'
      label='Medium'
      value={severityCounts.medium}
      color={SEV.medium}
      ink={SEV_INK.medium}
    />
    {includeLow
      ? (
        <SeverityBadge
          dense={dense}
          shortLabel='L'
          label='Low'
          value={severityCounts.low}
          color={SEV.low}
          ink={SEV_INK.low}
        />
        )
      : null}
  </Box>
)

/** Solid status badges for upgrades / meta labels (same weight as severity badges) */
export const StatusBadge = ({
  label,
  color,
  ink = '#fff',
  dense = false,
  hot = true
}) => (
  <Box
    title={label}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.4,
      px: dense ? 0.7 : 0.95,
      py: dense ? 0.3 : 0.45,
      borderRadius: 1.25,
      backgroundColor: hot ? color : alpha('#0a0c10', 0.85),
      color: hot ? ink : alpha('#fff', 0.38),
      border: hot
        ? `1px solid ${alpha('#fff', 0.22)}`
        : `1px solid ${alpha('#fff', 0.1)}`,
      boxShadow: hot
        ? `0 2px 10px ${alpha(color, 0.55)}, inset 0 1px 0 ${alpha('#fff', 0.22)}`
        : `inset 0 1px 0 ${alpha('#fff', 0.04)}`,
      fontWeight: 900,
      fontSize: dense ? '0.68rem' : '0.74rem',
      letterSpacing: 0.2,
      lineHeight: 1.1,
      whiteSpace: 'nowrap',
      maxWidth: '100%'
    }}
  >
    {label}
  </Box>
)

export const MetaChip = ({ label, tone = '#fff', dense = false, hot = true }) => {
  const ink = tone === UPGRADE.behind || tone === UPGRADE.missing || tone === SEV.medium || tone === SEV.low
    ? '#1a1208'
    : tone === UPGRADE.ok
      ? UPGRADE_INK.ok
      : tone === UPGRADE.unknown
        ? UPGRADE_INK.unknown
        : '#fff'
  return (
    <StatusBadge
      label={label}
      color={tone}
      ink={ink}
      dense={dense}
      hot={hot}
    />
  )
}

export const runtimeTone = (status) => {
  if (status === 'behind') return UPGRADE.behind
  if (status === 'ok') return UPGRADE.ok
  return UPGRADE.unknown
}
